import React from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  Navigate
} from 'react-router';
import { useSnackbar } from 'notistack';
import {
  Box, Card, CardHeader, CardContent, CardActions,
} from '@mui/material';
import useUser from 'hooks/useUser';
import { Button, Typography } from 'bso-ui';
import useConnectErrorHandler from 'helpers/errorHandlers';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog/DeleteConfirmationDialog';
import DRFTable from 'components/DRFTable/DRFTable';
import DRFRow from 'components/DRFTable/DRFRow';
import DRFCell from 'components/DRFTable/DRFCell';
import API from 'common/api';
import useLoading from 'context/LoadingContext';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import useDetail from 'hooks/useDetail';
import { PlaceHolderText } from 'components/PlaceholderCard/PlaceholderCard';
import PAGES from 'common/pages';
import { del } from 'helpers/query/fetchers';
import { VITE_PORTAL_API } from 'config';
import { allowActionOnUser, isAppAdmin } from 'helpers/permissions';
import UserToolbar from './UserToolbar';
import UserCreate from './Form/UserCreate';
import UserAssignGroup from './UserAssignGroup';

const UserInfo = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const height = '80vh';
  const [{ data: loggedUserData }] = useUser();
  const { handleError } = useConnectErrorHandler();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, setLoading } = useLoading();
  const query = {
    storeKey: `user-detail-${id}`,
    apiEndpoint: `${API.USER}${id}/`,
  };
  const [{
    data, isLoading, isFetching, isError, refetch
  }] = useDetail(query.storeKey, query.apiEndpoint);

  const deleteUser = () => {
    setLoading(true);
    const url = `${VITE_PORTAL_API}${API.USER}${id}/`;
    del(url)
      .then(() => {
        navigate(`${PAGES.USER_MGMT}${location.search}`);

        const msg = `USER ID: ${id} has been Deleted.`;

        enqueueSnackbar(msg, {
          content: (key) => <SnackMessage id={key} message={msg} variant="success" />
        });
      })
      .catch((err) => {
        setLoading(false);
        handleError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getActionButtons = () => (
    <>
      {data && (loggedUserData.is_staff || allowActionOnUser(data.groups, loggedUserData.groups)) ? (
        <UserCreate
          title="Update User"
          data={data}
          button={{
            content: 'Update',
            variant: 'standard',
            state: 'outlined',
            color: 'primary',
            disabled: isFetching
          }}
          formType="update"
          refetch={refetch}
        />
      ) : (
        <div style={{ paddingTop: '36px' }} />
      )}
      {data && loggedUserData.is_staff ? (
        <DeleteConfirmationDialog
          id="DeleteUserHeader"
          title="Delete User"
          contentText="Are you sure you want to delete this User?"
          confirmText="Yes"
        >
          {(confirm) => (
            <Button
              id="DeleteUserButton"
              color="primary"
              variant="standard"
              state="outlined"
              disabled={isFetching}
              onClick={confirm(deleteUser)}
            >
              Delete
            </Button>
          )}
        </DeleteConfirmationDialog>
      ) : (
        <div style={{ paddingTop: '36px' }} />
      )}
    </>
  );

  if (isError) {
    return <Navigate replace to={`${PAGES.USER_MGMT}${location.search}`} />;
  }

  if (data) {
    return (
      <Box p={3}>
        <UserToolbar
          title="User Information"
          isFetching={isFetching}
          refetch={refetch}
          backLink={PAGES.USER_MGMT}
          data={data}
          userData={loggedUserData}
        />
        <Card sx={{ marginBottom: '26px' }}>
          <CardHeader
            title={(
              <Typography variant="h4" color="primary">
                USER ID: {data.id}
              </Typography>
            )}
            style={{ paddingBottom: 0 }}
          />
          <CardContent
            style={{
              paddingTop: 0,
              overflowY: 'auto'
            }}
          >
            <div style={{ paddingBottom: '16px' }}>
              <Typography variant="subtitle1">{data.username}</Typography>
            </div>
            <DRFTable>
              <DRFRow>
                <DRFCell width={5}>
                  <Typography variant="body2">First Name</Typography>
                </DRFCell>
                <DRFCell width={7}>
                  <Typography>{data.first_name ? data.first_name : '---'}</Typography>
                </DRFCell>
              </DRFRow>
              <DRFRow>
                <DRFCell width={5}>
                  <Typography variant="body2">Last Name</Typography>
                </DRFCell>
                <DRFCell width={7}>
                  <Typography>{data.last_name ? data.last_name : '---'}</Typography>
                </DRFCell>
              </DRFRow>
              <DRFRow>
                <DRFCell width={5}>
                  <Typography variant="body2">Email</Typography>
                </DRFCell>
                <DRFCell width={7}>
                  <Typography>{data.email ? data.email : '---'}</Typography>
                </DRFCell>
              </DRFRow>
              <DRFRow>
                <DRFCell width={5}>
                  <Typography variant="body2">Staff</Typography>
                </DRFCell>
                <DRFCell width={7}>
                  <Typography>{data.is_staff ? 'Yes' : 'No'}</Typography>
                </DRFCell>
              </DRFRow>
              <DRFRow>
                <DRFCell width={5}>
                  <Typography variant="body2">Active</Typography>
                </DRFCell>
                <DRFCell width={7}>
                  <Typography>{data.is_active ? 'Yes' : 'No'}</Typography>
                </DRFCell>
              </DRFRow>
            </DRFTable>
          </CardContent>
          <CardActions sx={{ justifyContent: 'end' }}>{getActionButtons()}</CardActions>
        </Card>
        {data && loggedUserData && (isAppAdmin(loggedUserData.groups) || loggedUserData.is_staff)
        && <UserAssignGroup userData={data} refetchUser={refetch} loggedUserData={loggedUserData} />}
      </Box>
    );
  }

  if (isLoading || loading) return <PlaceHolderText content="Loading..." height={height} />;

  if (data === null || id === 'no-selected') {
    return <PlaceHolderText content="User ID Not found. Please select an item." height={height} />;
  }

  return <Navigate replace to={PAGES.NOT_FOUND} />;
};

export default UserInfo;
